import { Button } from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { QueryState } from "@/shared/ui/QueryState";
import { useFormQuery, useFormSubmissionsQuery } from "../api/queries";
import { FormSubmissionsFilter } from "../ui/FormSubmissionsFilter";
import { FormSubmissionsTable } from "../ui/FormSubmissionsTable";

export function FormSubmissionsPage() {
  const nav = useNavigate();
  const { id: formId } = useParams({ from: "/app/forms/$id/submissions" });
  const search = useSearch({
    from: "/app/forms/$id/submissions",
  }) as OffsetPageQueryDTO;

  const formQuery = useFormQuery(formId ?? "");
  const list = useFormSubmissionsQuery(formId ?? "", {
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const limit = search.limit ?? 20;
  const offset = search.offset ?? 0;

  return (
    <>
      <Header />
      <Main>
        <QueryState
          result={formQuery}
          variant="fullscreen"
          isEmpty={!formQuery.data}
          emptyFallback={
            <div className="text-center">
              <p className="text-muted-foreground">ບໍ່ພົບຟອມ</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => nav({ to: "/app/forms" })}
              >
                ກັບລາຍການ
              </Button>
            </div>
          }
        >
          {formQuery.data && (
            <div className="space-y-6">
              <div className="flex gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h1 className="font-bold text-2xl tracking-tight">
                    ລາຍການສົ່ງຟອມ
                  </h1>
                  <p className="mt-1 text-muted-foreground">
                    {formQuery.data.name}
                  </p>
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    nav({
                      to: "/app/forms/$id",
                      params: { id: formId ?? "" },
                    })
                  }
                  className="mb-2 -ml-2"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  ກັບລາຍລະອຽດຟອມ
                </Button>
              </div>

              <div className="flex flex-col rounded-xl border bg-card pt-2">
                <FormSubmissionsFilter />
                <FormSubmissionsTable
                  data={list.data?.data ?? []}
                  isLoading={list.isLoading}
                  offset={offset}
                  limit={limit}
                  totalCount={list.data?.meta.total ?? 0}
                  onPaginationChange={(nextOffset, nextLimit) =>
                    nav({
                      to: "/app/forms/$id/submissions",
                      params: { id: formId ?? "" },
                      search: {
                        ...search,
                        offset: nextOffset,
                        limit: nextLimit,
                      },
                    })
                  }
                  sortBy={search.sort ? search.sort[0]?.field : undefined}
                  sortOrder={
                    search.sort
                      ? (search.sort[0]?.dir as "asc" | "desc")
                      : undefined
                  }
                  onSortingChange={(field, desc) =>
                    nav({
                      to: "/app/forms/$id/submissions",
                      params: { id: formId ?? "" },
                      search: {
                        ...search,
                        sort: [{ field, dir: desc ? "desc" : "asc" }],
                      },
                    })
                  }
                  onViewDetail={(submissionId) =>
                    nav({
                      to: "/app/forms/$id/submissions/$submissionId",
                      params: {
                        id: formId ?? "",
                        submissionId,
                      },
                    })
                  }
                />
              </div>
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}
